import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Clock, DollarSign, Users, Flag, Star,
  Ruler, CalendarCheck, Sparkles, ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatCityName } from "@/lib/utils";

interface SharedData {
  business: string;
  city: string;
  timeline: string;
  budget: string;
  score: number;
  sqft: string;
  seats: number;
  team: string;
  serviceStyle: string;
  totalEstimate: number;
  totalMonthlyLabor: number;
  staffing: { role: string; count: number; priority: string }[];
  costBreakdown: { category: string; estimate: number; percentage: number }[];
  milestones: { label: string; weeksBeforeOpen: number; critical: boolean }[];
  timelineSteps: { step: string; weekStart: number; weekEnd: number }[];
  totalWeeks: number;
}

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] },
});

export default function SharedRoadmap() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<SharedData | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    try {
      const encoded = searchParams.get("d");
      if (!encoded) { setError(true); return; }
      const decoded = JSON.parse(decodeURIComponent(escape(atob(encoded)))) as SharedData;
      setData(decoded);
    } catch {
      setError(true);
    }
  }, [searchParams]);

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <Sparkles className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-medium text-gray-900 mb-2">Roadmap not found</h1>
          <p className="text-gray-500 mb-6">This link may have expired or is invalid.</p>
          <a href="/">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
              Create your own roadmap <ArrowRight className="w-4 h-4" />
            </Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-emerald-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.15),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(16,185,129,0.08),transparent_50%)]" />

        <div className="relative container max-w-4xl py-16 px-6">
          <motion.div {...fade()}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 text-xs font-medium text-emerald-300 mb-6">
              <Sparkles className="w-3.5 h-3.5" /> AI-Generated Opening Roadmap
            </div>
          </motion.div>

          <motion.h1 {...fade(0.1)} className="text-4xl sm:text-5xl font-medium text-white leading-tight mb-3">
            {data.business}
            <span className="block text-emerald-400 mt-1">in {formatCityName(data.city)}</span>
          </motion.h1>

          <motion.p {...fade(0.15)} className="text-lg text-gray-300 max-w-xl mb-10">
            A complete roadmap with budget, staffing, permits, and timeline — generated by Oplif.
          </motion.p>

          {/* Stat pills */}
          <motion.div {...fade(0.2)} className="flex flex-wrap gap-3">
            {[
              { icon: Clock, label: data.timeline },
              { icon: DollarSign, label: data.budget },
              { icon: Star, label: `Score: ${data.score}/100` },
              { icon: Ruler, label: data.sqft },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 text-sm text-white">
                <s.icon className="w-4 h-4 text-emerald-400" /> {s.label}
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="container max-w-4xl px-6 py-12 space-y-10">
        {/* Timeline Gantt */}
        <motion.section {...fade(0.1)}>
          <SectionHeader icon={CalendarCheck} title="Opening Timeline" />
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="space-y-1.5">
              {data.timelineSteps.map((phase, i) => {
                const leftPct = Math.min((phase.weekStart / data.totalWeeks) * 100, 100);
                const widthPct = Math.max(Math.min(((phase.weekEnd - phase.weekStart) / data.totalWeeks) * 100, 100 - leftPct), 4);
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-36 text-xs text-gray-500 truncate shrink-0">{phase.step}</div>
                    <div className="flex-1 h-7 bg-gray-100 rounded relative">
                      <div
                        className="absolute top-0.5 bottom-0.5 rounded bg-emerald-500"
                        style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">
                        W{phase.weekStart}–{phase.weekEnd}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex justify-between text-[10px] text-gray-400">
              <span>Week 0</span><span>Week {data.totalWeeks}</span>
            </div>
          </div>
        </motion.section>

        {/* Cost Breakdown */}
        <motion.section {...fade(0.15)}>
          <SectionHeader icon={DollarSign} title="Startup Cost Breakdown" />
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="space-y-3">
              {data.costBreakdown.map(c => (
                <div key={c.category} className="flex items-center gap-3">
                  <div className="flex-1 text-sm text-gray-600">{c.category}</div>
                  <div className="w-32 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${c.percentage}%` }} />
                  </div>
                  <div className="text-sm font-medium text-gray-900 w-20 text-right">${c.estimate.toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-gray-100 flex justify-between">
              <span className="font-medium text-gray-900">Total Estimated Cost</span>
              <span className="text-lg font-medium text-emerald-600">${data.totalEstimate.toLocaleString()}</span>
            </div>
          </div>
        </motion.section>

        {/* Staffing */}
        <motion.section {...fade(0.2)}>
          <SectionHeader icon={Users} title="Staffing Plan" />
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.staffing.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{s.role}</span>
                    {s.count > 1 && <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">×{s.count}</span>}
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                    s.priority === "essential" ? "bg-emerald-100 text-emerald-700" :
                    s.priority === "recommended" ? "bg-amber-100 text-amber-700" :
                    "bg-gray-100 text-gray-500"
                  }`}>
                    {s.priority}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-gray-100 flex justify-between">
              <span className="font-medium text-gray-900">Est. Monthly Labor</span>
              <span className="text-lg font-medium text-emerald-600">${data.totalMonthlyLabor.toLocaleString()}/mo</span>
            </div>
          </div>
        </motion.section>

        {/* Milestones */}
        <motion.section {...fade(0.25)}>
          <SectionHeader icon={Flag} title="Key Milestones" />
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="relative">
              <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-200" />
              <div className="space-y-3">
                {data.milestones.map((m, i) => (
                  <div key={i} className="flex items-center gap-4 pl-1">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 z-10 bg-white ${m.critical ? "border-emerald-500" : "border-gray-200"}`}>
                      {m.critical && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                    </div>
                    <div className="flex-1 flex items-center justify-between">
                      <span className={`text-sm ${m.critical ? "font-medium text-gray-900" : "text-gray-500"}`}>{m.label}</span>
                      <span className="text-xs text-gray-400">{m.weeksBeforeOpen === 0 ? "Opening Day" : `${m.weeksBeforeOpen}w before`}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section {...fade(0.3)}>
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 to-emerald-900 p-10 text-center">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(16,185,129,0.12),transparent_70%)]" />
            <div className="relative">
              <Sparkles className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-2xl sm:text-3xl font-medium text-white mb-3">
                Create your own opening roadmap
              </h2>
              <p className="text-gray-300 mb-6 max-w-md mx-auto">
                Get a personalized plan with permits, budget, staffing, and timeline — in under 5 minutes.
              </p>
              <a href="/">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white h-12 px-8 text-base font-medium rounded-xl gap-2 shadow-lg shadow-emerald-500/20">
                  Get started free <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
              <p className="text-xs text-gray-500 mt-4">No credit card required</p>
            </div>
          </div>
        </motion.section>

        {/* Disclaimer + branding */}
        <div className="text-center pt-4 pb-8">
          <p className="text-xs text-gray-400 mb-2">
            This roadmap is an AI-generated estimate — not financial or legal advice.
          </p>
          <a href="/" className="inline-flex items-center gap-1 text-sm font-medium text-gray-900">
            oplif<span className="text-emerald-500">.</span>
          </a>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title }: { icon: any; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-5 h-5 text-emerald-500" />
      <h2 className="text-lg font-medium text-gray-900">{title}</h2>
    </div>
  );
}
